//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  DetentionHandlingGlobalStyle,
} from './DetentionHandling.styles';

const hero = {
  slug: 'detention-handling',
  title: 'Detention Handling',
  description:
    'Warns the dock before a truck runs past its free time, reports the detention you owe, and approves a carrier’s detention request when it matches the gate record.',
  metric: '50–65%',
  metricLabel: 'lower detention fees',
};

const watchItRun = {
  title: 'A truck is about to run out of free time',
  stageHeadTitle: 'A truck on #LS-53018 has been waiting 90 minutes for a door.',
  stageHeadMeta: 'Detention · #LS-53018',
  rows: [
    {
      time: '15:30:00',
      text: 'Truck on #LS-53018 has waited 90 min for a door. 30 min of free time left.',
    },
    {
      time: '15:30:04',
      text: 'Dock lead alerted with the truck, the dock and the time left.',
    },
    {
      time: '20:02:00',
      text: 'Truck checked out. 6h 02m on site, 4h 02m past free time.',
    },
    {
      time: '20:14:30',
      text: 'Carrier’s detention request arrived: 4h 02m at $40/hr.',
    },
    {
      time: '20:14:35',
      text: 'Matched to the gate record. Approved at $161.33 and logged to the facility’s report.',
    },
  ],
  summaryText:
    'A truck sat waiting for a door. Loadsmart AI warned the dock lead before the free time ran out. When the carrier still billed detention, it checked the request against the gate times and the contract rate, approved the amount that matched, and added it to the facility’s detention report.',
  agentBadge: 'Detention Agent',
  escalationTitle: 'Disputes go to a person.',
  escalationText:
    'Requests that match the gate record are approved at the contract rate. A request that claims more time than the record shows gets the supported time back, with the timestamps. When a carrier pushes back, or the record has a gap such as a missed check-out scan, a Loadsmart Freight Expert takes it with the full timeline.',
};

const howItWorks = {
  stepsTitleWhite: 'Watches the clock on every truck,',
  stepsTitleGradient: 'and settles the bill.',
  steps: [
    {
      title: 'Watches dwell against free time',
      description:
        'Checks every truck on site every 15 minutes against its free time, using your gate, yard or scheduling data.',
    },
    {
      title: 'Warns before the clock runs out',
      description:
        'Alerts the dock lead when a truck has waited too long for a door, or has been at the door too long, with the truck, the dock and the time left.',
    },
    {
      title: 'Reports what you owe',
      description:
        'Keeps a running report of detention owed, by carrier and by facility.',
    },
    {
      title: 'Decides the detention request',
      description:
        'Approves a request that matches the gate record, and returns one that doesn’t with the timestamps.',
    },
  ],
  outcomes: [
    {
      title: 'Less detention to pay',
      description:
        'Trucks close to the end of their free time get a door first, because the dock knows which ones they are.',
    },
    {
      title: 'Requests settled in minutes',
      description:
        'Each request is checked against the gate record when it arrives, so nobody rebuilds a timeline days later.',
    },
    {
      title: 'You know what detention costs',
      description:
        'A report by carrier and by facility shows where the money goes and which docks cause it.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'Checks the rest of the carrier’s invoice against the contracted rate.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Rebooks a late truck so the dock plan holds.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
  {
    slug: 'warehouse-customer-service',
    title: 'Warehouse Customer Service',
    description:
      'We answer your warehouse’s status questions and book appointments from an email, using your dock schedule and WMS.',
  },
];

function DetentionHandling() {
  return (
    <PageWrapper>
      <DetentionHandlingGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Detention Handling" />
      <Footer />
    </PageWrapper>
  );
}

export default DetentionHandling;
export { DetentionHandling };
